/** Instructor tier pricing rows — extra sort keys for the System → Packages tab. */
export const INSTRUCTOR_PLAN_SORT_FIELDS = {
  tier: 'tier',
  name: 'name',
  monthlyCents: 'monthlyCents',
  annualCents: 'annualCents',
  courses: 'courses',
  students: 'students',
  assessments: 'assessments',
  price: 'price',
  inAppPurchase: 'inAppPurchase',
};

/** Default sort for instructor plan rows — cheapest monthly price first. */
export const INSTRUCTOR_PLAN_LIST_DEFAULTS = {
  sortBy: 'monthlyCents',
  sortDir: 'asc',
};

const INSTRUCTOR_NUMERIC_FIELDS = new Set([
  INSTRUCTOR_PLAN_SORT_FIELDS.monthlyCents,
  INSTRUCTOR_PLAN_SORT_FIELDS.annualCents,
  INSTRUCTOR_PLAN_SORT_FIELDS.courses,
  INSTRUCTOR_PLAN_SORT_FIELDS.students,
  INSTRUCTOR_PLAN_SORT_FIELDS.assessments,
  INSTRUCTOR_PLAN_SORT_FIELDS.price,
]);

/** Resolves instructor plan row value for client-side sorting. */
function instructorPlanSortValue(row, sortBy) {
  switch (sortBy) {
    case INSTRUCTOR_PLAN_SORT_FIELDS.tier:
      return row.tier ?? '';
    case INSTRUCTOR_PLAN_SORT_FIELDS.monthlyCents:
    case INSTRUCTOR_PLAN_SORT_FIELDS.annualCents:
    case INSTRUCTOR_PLAN_SORT_FIELDS.courses:
    case INSTRUCTOR_PLAN_SORT_FIELDS.students:
    case INSTRUCTOR_PLAN_SORT_FIELDS.assessments:
    case INSTRUCTOR_PLAN_SORT_FIELDS.price:
      return Number(row[sortBy]) || 0;
    case INSTRUCTOR_PLAN_SORT_FIELDS.inAppPurchase:
      return row.inAppPurchase ?? '';
    case INSTRUCTOR_PLAN_SORT_FIELDS.name:
    default:
      return row.name ?? '';
  }
}

/** Sorts instructor plan rows in memory. */
export function sortInstructorPlansLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || INSTRUCTOR_PLAN_LIST_DEFAULTS.sortBy;
  const numeric = INSTRUCTOR_NUMERIC_FIELDS.has(field);

  list.sort((a, b) => {
    const av = instructorPlanSortValue(a, field);
    const bv = instructorPlanSortValue(b, field);
    if (numeric || (typeof av === 'number' && typeof bv === 'number')) {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}

/** Learner app plan rows — same pricing sort keys plus questions. */
export const LEARNER_PLAN_SORT_FIELDS = {
  ...INSTRUCTOR_PLAN_SORT_FIELDS,
  questions: 'questions',
};

export const LEARNER_PLAN_LIST_DEFAULTS = {
  sortBy: 'monthlyCents',
  sortDir: 'asc',
};

const LEARNER_NUMERIC_FIELDS = new Set([
  ...INSTRUCTOR_NUMERIC_FIELDS,
  LEARNER_PLAN_SORT_FIELDS.questions,
]);

/** Sorts learner plan rows in memory. */
export function sortLearnerPlansLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || LEARNER_PLAN_LIST_DEFAULTS.sortBy;
  const numeric = LEARNER_NUMERIC_FIELDS.has(field);

  list.sort((a, b) => {
    const av = instructorPlanSortValue(a, field === 'questions' ? 'questions' : field);
    const bv = instructorPlanSortValue(b, field === 'questions' ? 'questions' : field);
    if (field === LEARNER_PLAN_SORT_FIELDS.questions) {
      const aq = Number(a.questions) || 0;
      const bq = Number(b.questions) || 0;
      return (aq - bq) * dir;
    }
    if (numeric || (typeof av === 'number' && typeof bv === 'number')) {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}

/** Default sort used by the packages list screen (client-side only). */
export const PACKAGES_LIST_DEFAULTS = {
  sortBy: 'name',
  sortDir: 'asc',
};

/** Sort keys exposed by the packages table column headers. */
export const PACKAGES_SORT_FIELDS = {
  name: 'name',
  planId: 'planId',
  instructors: 'instructors',
  courses: 'courses',
  assessments: 'assessments',
  numberOfProctor: 'numberOfProctor',
  students: 'students',
  numberOfQuestions: 'numberOfQuestions',
  price: 'price',
  inAppPurchase: 'inAppPurchase',
};

/**
 * Computes the next client-side sort when a column header is clicked.
 * Same column toggles asc/desc; a new column always starts asc.
 */
export function getNextPackagesSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || PACKAGES_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return {
      sortBy,
      sortDir: currentSortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy, sortDir: 'asc' };
}

const NUMERIC_PACKAGE_FIELDS = new Set([
  PACKAGES_SORT_FIELDS.instructors,
  PACKAGES_SORT_FIELDS.courses,
  PACKAGES_SORT_FIELDS.assessments,
  PACKAGES_SORT_FIELDS.numberOfProctor,
  PACKAGES_SORT_FIELDS.students,
  PACKAGES_SORT_FIELDS.numberOfQuestions,
  PACKAGES_SORT_FIELDS.price,
]);

/** Resolves a row field value for client-side column sorting. */
function packageSortValue(row, sortBy) {
  switch (sortBy) {
    case PACKAGES_SORT_FIELDS.planId:
      return row.planId ?? '';
    case PACKAGES_SORT_FIELDS.instructors:
    case PACKAGES_SORT_FIELDS.courses:
    case PACKAGES_SORT_FIELDS.assessments:
    case PACKAGES_SORT_FIELDS.numberOfProctor:
    case PACKAGES_SORT_FIELDS.students:
    case PACKAGES_SORT_FIELDS.numberOfQuestions:
    case PACKAGES_SORT_FIELDS.price:
      return Number(row[sortBy]) || 0;
    case PACKAGES_SORT_FIELDS.inAppPurchase:
      return row.inAppPurchase ?? '';
    case PACKAGES_SORT_FIELDS.name:
    default:
      return row.name ?? '';
  }
}

/**
 * Sorts package rows in memory — no API round-trip.
 */
export function sortPackagesLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || PACKAGES_LIST_DEFAULTS.sortBy;
  const numeric = NUMERIC_PACKAGE_FIELDS.has(field);

  list.sort((a, b) => {
    const av = packageSortValue(a, field);
    const bv = packageSortValue(b, field);
    if (numeric || (typeof av === 'number' && typeof bv === 'number')) {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}
